import { inject } from '@angular/core';
import { patchState, signalStoreFeature, withMethods } from '@ngrx/signals';
import { withSnackBarFeature } from '@data-access/shared/store/features/with-snack-bar-features.store';
import { DocumentsService } from '@data-access/documents/services';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { switchMap, mergeMap, pipe, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { openFileInNewTab, saveAs } from '@shared/utils/download.util';
import { HttpParams } from '@angular/common/http';

export const documentFeatureMethods = () =>
  signalStoreFeature(
    withSnackBarFeature(),
    withMethods(
      (
        { displaySnackBar, ...store },
        _documentService = inject(DocumentsService),
      ) => {
        const showSnackbar = (
          message: string,
          panelClass: string,
          duration = 5000,
        ): void => {
          displaySnackBar(message, 'Close', duration, panelClass);
        };
        const _openDocumentInNewTab = rxMethod<string>(
          pipe(
            tap(() => patchState(store, { error: null })),
            mergeMap((documentId) =>
              _documentService.downloadDocument(documentId).pipe(
                tapResponse({
                  error: (error: Error) => patchState(store, { error }),
                  next: ({ blob }) => openFileInNewTab(blob),
                }),
              ),
            ),
          ),
        );
        const _downloadDocument = rxMethod<{
          documentId: string;
          filename: string;
        }>(
          pipe(
            tap(() => patchState(store, { error: null })),
            mergeMap(({ documentId, filename }) => {
              showSnackbar(
                `Downloading file "${filename}"...`,
                'snackbar-loading',
              );
              return _documentService.downloadDocument(documentId).pipe(
                tapResponse({
                  error: (error: Error) => {
                    patchState(store, { error });
                    // Use duration 0 (sticky) so a concurrent success toast cannot
                    // overwrite this error before the user has a chance to read it.
                    showSnackbar(
                      `Failed to download file "${filename}". Please try downloading the file again. If the issue persists, please contact WCG Client Care as listed under Contact Us.`,
                      'snackbar-error',
                      0,
                    );
                  },
                  next: ({ blob, filename: serverFilename }) => {
                    saveAs(blob, serverFilename);
                    showSnackbar(
                      `Downloaded file "${filename}".`,
                      'snackbar-success',
                    );
                  },
                }),
              );
            }),
          ),
        );

        const _deleteDocument = rxMethod<{
          workspaceId: string;
          currentStage: string;
          documentId: string;
          onSuccess?: () => void;
        }>(
          pipe(
            tap(() => patchState(store, { error: null, loading: true })),
            switchMap(({ workspaceId, currentStage, documentId, onSuccess }) =>
              _documentService
                .deleteDocument(workspaceId, currentStage, documentId)
                .pipe(
                  tapResponse({
                    error: (error: Error) =>
                      patchState(store, { error, loading: false }),
                    next: () => {
                      patchState(store, { loading: false });
                      onSuccess?.();
                    },
                  }),
                ),
            ),
          ),
        );

        const _bulkDeleteDocument = rxMethod<{
          workspaceId: string;
          documentIds: string;
          currentStage: string;
          onSuccess?: () => void;
        }>(
          pipe(
            tap(() => patchState(store, { error: null, loading: true })),
            switchMap(
              ({ workspaceId, currentStage, documentIds, onSuccess }) => {
                const params = new HttpParams().set(
                  'document_ids',
                  documentIds,
                );
                return _documentService
                  .bulkDeleteDocument(workspaceId, currentStage, params)
                  .pipe(
                    tapResponse({
                      next: () => {
                        patchState(store, { loading: false });
                        onSuccess?.();
                      },
                      error: (error: Error) => {
                        patchState(store, { error, loading: false });
                      },
                    }),
                  );
              },
            ),
          ),
        );

        return {
          applyDeleteDocument: (
            workspaceId: string,
            currentStage: string,
            documentId: string,
            onSuccess?: () => void,
          ) =>
            _deleteDocument({
              workspaceId,
              currentStage,
              documentId,
              onSuccess,
            }),
          applyBulkDeleteDocument: (
            workspaceId: string,
            currentStage: string,
            documentIds: string,
            onSuccess?: () => void,
          ) =>
            _bulkDeleteDocument({
              workspaceId,
              currentStage,
              documentIds,
              onSuccess,
            }),
          downloadDocument: (documentId: string, filename: string) =>
            _downloadDocument({ documentId, filename }),
          openInNewTab: (documentId: string) =>
            _openDocumentInNewTab(documentId),
        };
      },
    ),
  );
